import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { SpinnerGap, User, Envelope, Phone, MapPin, Buildings, Calendar, IdentificationCard, Shield, Tag, CurrencyEur, PencilSimple, FloppyDisk, X, Prohibit, CheckCircle, PaperPlaneTilt, Target, Storefront, Compass } from '@phosphor-icons/react';

const fmt = (v) => new Intl.NumberFormat('pt-PT', { style: 'currency', currency: 'EUR' }).format(v || 0);
const fmtN = (v) => new Intl.NumberFormat('pt-PT', { minimumFractionDigits: 2 }).format(v || 0);

function InfoRow({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-3">
      <Icon size={16} className="text-[#6B7280] mt-0.5 shrink-0" />
      <div>
        <p className="text-xs uppercase tracking-[0.15em] font-medium text-[#6B7280]">{label}</p>
        <p className="text-sm text-[#111827]">{value || 'Não existem dados'}</p>
      </div>
    </div>
  );
}

function Section({ title, testId, children }) {
  return (
    <div data-testid={testId} className="bg-white border border-[#E5E7EB] rounded-lg overflow-hidden mb-4">
      <div className="px-5 py-3 border-b border-[#E5E7EB] bg-[#F9FAFB]">
        <h2 className="text-sm font-semibold text-[#111827]">{title}</h2>
      </div>
      {children}
    </div>
  );
}

export default function ProfilePage() {
  const { activeId, viewingUser } = useAuth();
  const [profile, setProfile] = useState(null);
  const [subscricoes, setSubscricoes] = useState([]);
  const [valesData, setValesData] = useState({ vales: [], Total: 0, Disponivel: 0, Gasto: 0 });
  const [mediacenter, setMediacenter] = useState([]);
  const [comissoes, setComissoes] = useState({});
  const [prefs, setPrefs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingDreams, setEditingDreams] = useState(false);
  const [dreamsForm, setDreamsForm] = useState({ NecessidadesSonhos: '', Preferencias: '' });
  const [savingDreams, setSavingDreams] = useState(false);

  const isViewingOther = !!viewingUser;

  useEffect(() => {
    setLoading(true);
    setEditingDreams(false);
    Promise.all([
      api.post('/perfil/get').then(r => {
        setProfile(r.data);
        setDreamsForm({ NecessidadesSonhos: r.data?.NecessidadesSonhos || '', Preferencias: r.data?.Preferencias || '' });
      }).catch(() => {}),
      api.post('/perfil/subscricoes').then(r => setSubscricoes(Array.isArray(r.data) ? r.data : [])).catch(() => {}),
      api.post('/perfil/vales').then(r => setValesData(r.data || { vales: [], Total: 0, Disponivel: 0, Gasto: 0 })).catch(() => {}),
      api.post('/perfil/mediacenter').then(r => setMediacenter(Array.isArray(r.data) ? r.data : [])).catch(() => {}),
      api.post('/perfil/comissoes').then(r => setComissoes(r.data || {})).catch(() => {}),
      api.post('/perfil/preferencias').then(r => setPrefs(r.data || null)).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, [activeId]);

  const handleSaveDreams = async () => {
    setSavingDreams(true);
    try {
      await api.post('/perfil/editDreamsPref', { ID_Cliente: activeId, ...dreamsForm });
      setEditingDreams(false);
      setProfile(prev => ({ ...prev, NecessidadesSonhos: dreamsForm.NecessidadesSonhos, Preferencias: dreamsForm.Preferencias }));
    } catch (e) { console.error(e); }
    setSavingDreams(false);
  };

  // ── Activar / Desactivar cliente (só visível quando estamos a ver outro) ──
  const [togglingActive, setTogglingActive] = useState(false);
  const handleToggleActive = async () => {
    if (!profile?.ID_Cliente) return;
    const isActive = (profile.Activo || 'S').toUpperCase() === 'S';
    const action = isActive ? 'DESACTIVAR' : 'REACTIVAR';
    const confirmed = window.confirm(
      `Tem a certeza que quer ${action} o cliente "${profile.NomeCompleto}"?\n\n` +
      (isActive
        ? 'Esta acção marca o cliente como inactivo (soft-delete). Poderá reactivar depois se necessário.'
        : 'O cliente voltará a estar activo no sistema.')
    );
    if (!confirmed) return;
    setTogglingActive(true);
    try {
      const r = await api.post('/perfil/toggle-active', { ID_Cliente: profile.ID_Cliente });
      if (r.data?.success) {
        setProfile(prev => ({ ...prev, Activo: r.data.Activo }));
      } else {
        window.alert(r.data?.message || 'Erro ao actualizar cliente.');
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || 'Erro desconhecido';
      window.alert(`Não foi possível alterar o estado do cliente:\n${msg}`);
    }
    setTogglingActive(false);
  };

  if (loading) return <div className="flex justify-center py-20"><SpinnerGap size={28} className="animate-spin text-[#0F1829]" /></div>;

  const p = profile || {};
  const contactos = p.Contactos || [];
  const vales = valesData.vales || [];

  return (
    <div data-testid="profile-page" style={{ fontFamily: "'Manrope', sans-serif" }}>
      <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-[#111827]" style={{ fontFamily: "'Fraunces', sans-serif" }}>Perfil</h1>
          <p className="text-sm text-[#6B7280] mt-1">Página onde pode ver o seu perfil</p>
        </div>
        {!isViewingOther && (
          <Link to="/perfil/email-config" data-testid="profile-email-config-link"
            className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-2 rounded border border-[#E5E7EB] hover:bg-[#F3F4F6] text-[#0F1829]">
            <PaperPlaneTilt size={13} /> Config. de Email SMTP
          </Link>
        )}
        {isViewingOther && p.ID_Cliente && (
          (() => {
            const isActive = (p.Activo || 'S').toUpperCase() === 'S';
            return (
              <div className="flex items-center gap-2">
                <span
                  data-testid="profile-active-badge"
                  className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.15em] font-semibold px-2.5 py-1.5 rounded ${
                    isActive
                      ? 'bg-[#E7F5EB] text-[#1F7A3B]'
                      : 'bg-[#F3F4F6] text-[#6B7280] line-through'
                  }`}
                >
                  {isActive ? <CheckCircle size={12} weight="fill" /> : <Prohibit size={12} weight="fill" />}
                  {isActive ? 'Activo' : 'Inactivo'}
                </span>
                <button
                  data-testid="profile-toggle-active-btn"
                  onClick={handleToggleActive}
                  disabled={togglingActive}
                  className={`inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded border transition ${
                    isActive
                      ? 'bg-white border-[#DC2626] text-[#DC2626] hover:bg-[#DC2626] hover:text-white'
                      : 'bg-white border-[#1F7A3B] text-[#1F7A3B] hover:bg-[#1F7A3B] hover:text-white'
                  } disabled:opacity-50 disabled:cursor-wait`}
                  title={isActive ? 'Desactivar este cliente (soft-delete)' : 'Reactivar este cliente'}
                >
                  {togglingActive
                    ? <SpinnerGap size={14} className="animate-spin" />
                    : (isActive ? <Prohibit size={14} weight="bold" /> : <CheckCircle size={14} weight="bold" />)}
                  {isActive ? 'Desactivar cliente' : 'Reactivar cliente'}
                </button>
              </div>
            );
          })()
        )}
      </div>

      {/* 1. DETALHE DO CLIENTE */}
      <Section title="Detalhe do cliente" testId="profile-info">
        <div className="p-5">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div>
              <h3 className="text-sm font-semibold text-[#111827] text-center mb-4">Informação</h3>
              <div className="space-y-3">
                <InfoRow icon={User} label="Cliente recomendação" value={p.ClienteRecNome} />
                <InfoRow icon={IdentificationCard} label="ID - Nome" value={p.ID_Cliente ? `${p.ID_Cliente} - ${p.NomeCompleto}` : '-'} />
                <InfoRow icon={Tag} label="Tipo de Cliente" value={p.TipoCliente} />
                <InfoRow icon={Shield} label="Tipo de Entidade" value={p.Particular === 'S' ? 'Particular' : p.Particular === 'N' ? 'Empresarial' : '-'} />
                <InfoRow icon={Envelope} label="Email" value={p.Email} />
                <InfoRow icon={MapPin} label="Localidade" value={p.Localidade} />
                <InfoRow icon={Calendar} label="Nasceu em" value={p.DataNascimento} />
                <InfoRow icon={Calendar} label="Registado no IHTP desde" value={p.DataRegisto} />
                <InfoRow icon={Calendar} label="Data de Validade do Contrato" value={p.DataValidade} />
                <InfoRow icon={Buildings} label="Empresa" value={p.Empresa} />
                <InfoRow icon={IdentificationCard} label="NIF" value={p.Nif} />
                <InfoRow icon={CurrencyEur} label="Créditos" value={p.CreditosComissao != null ? fmtN(p.CreditosComissao) : '0'} />
              </div>
              {/* Comissões */}
              <div className="mt-6 pt-4 border-t border-[#E5E7EB]">
                <h3 className="text-sm font-semibold text-[#111827] text-center mb-3">Comissões</h3>
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-[#F9FAFB] rounded-lg p-3 text-center">
                    <p className="text-[10px] uppercase tracking-[0.15em] font-medium text-[#6B7280]">Ganhas</p>
                    <p className="text-sm font-mono font-semibold text-[#111827]">{fmt(comissoes.Ganhas)}</p>
                  </div>
                  <div className="bg-[#F9FAFB] rounded-lg p-3 text-center">
                    <p className="text-[10px] uppercase tracking-[0.15em] font-medium text-[#6B7280]">Pagas</p>
                    <p className="text-sm font-mono font-semibold text-[#111827]">{fmt(comissoes.Pagas)}</p>
                  </div>
                  <div className="bg-[#F9FAFB] rounded-lg p-3 text-center">
                    <p className="text-[10px] uppercase tracking-[0.15em] font-medium text-[#6B7280]">Disponível p/ Pagamento</p>
                    <p className="text-sm font-mono font-semibold text-[#0F1829]">{fmt(comissoes.DisponivelPagamento)}</p>
                  </div>
                  <div className="bg-[#F9FAFB] rounded-lg p-3 text-center">
                    <p className="text-[10px] uppercase tracking-[0.15em] font-medium text-[#6B7280]">A Receber</p>
                    <p className="text-sm font-mono font-semibold text-[#0F1829]">{fmt(comissoes.Receber)}</p>
                  </div>
                </div>
              </div>
            </div>
            <div>
              <h3 className="text-sm font-semibold text-[#111827] text-center mb-4">Contactos</h3>
              {contactos.length > 0 ? (
                <div className="space-y-3">
                  {contactos.map((c, i) => (
                    <div key={`${c.TipoContacto || 'tc'}-${c.Contacto || 'c'}-${i}`} data-testid={`contacto-${i}`} className="flex items-start gap-3">
                      <Phone size={16} className="text-[#6B7280] mt-0.5 shrink-0" />
                      <div>
                        <p className="text-xs uppercase tracking-[0.15em] font-medium text-[#6B7280]">{c.TipoContacto}</p>
                        <p className="text-sm text-[#111827]">{c.Contacto || '-'}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : <p className="text-sm text-[#6B7280] text-center">Não existem contactos disponíveis</p>}
            </div>
          </div>
        </div>
      </Section>

      {/* NECESSIDADES/SONHOS E PREFERÊNCIAS (só visível pela upline) */}
      {isViewingOther && (
        <Section title="Necessidades / Sonhos e Preferências" testId="profile-dreams">
          <div className="p-5">
            <div className="flex items-center justify-end mb-3">
              {!editingDreams ? (
                <button data-testid="edit-dreams-btn" onClick={() => setEditingDreams(true)}
                  className="flex items-center gap-1 text-xs font-medium text-[#0F1829] hover:underline">
                  <PencilSimple size={14} /> Editar
                </button>
              ) : (
                <div className="flex gap-2">
                  <button data-testid="save-dreams-btn" onClick={handleSaveDreams} disabled={savingDreams}
                    className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium bg-[#0F1829] text-white rounded hover:bg-[#001f7a] disabled:opacity-50">
                    <FloppyDisk size={14} /> {savingDreams ? 'A guardar...' : 'Guardar'}
                  </button>
                  <button data-testid="cancel-dreams-btn" onClick={() => { setEditingDreams(false); setDreamsForm({ NecessidadesSonhos: p.NecessidadesSonhos || '', Preferencias: p.Preferencias || '' }); }}
                    className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium border border-[#E5E7EB] text-[#374151] rounded hover:bg-[#F9FAFB]">
                    <X size={14} /> Cancelar
                  </button>
                </div>
              )}
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div>
                <p className="text-xs uppercase tracking-[0.15em] font-medium text-[#6B7280] mb-2">Necessidades / Sonhos</p>
                {!editingDreams ? (
                  <p data-testid="dreams-value" className="text-sm text-[#111827] bg-[#F9FAFB] rounded-lg p-3 min-h-[80px]">{p.NecessidadesSonhos || 'Não existem dados'}</p>
                ) : (
                  <textarea data-testid="dreams-input" value={dreamsForm.NecessidadesSonhos} onChange={e => setDreamsForm({ ...dreamsForm, NecessidadesSonhos: e.target.value })}
                    className="w-full px-3 py-2 text-sm border border-[#E5E7EB] rounded-lg focus:ring-2 focus:ring-[#0F1829] outline-none resize-none" rows={4} placeholder="Necessidades / Sonhos..." />
                )}
              </div>
              <div>
                <p className="text-xs uppercase tracking-[0.15em] font-medium text-[#6B7280] mb-2">Preferências</p>
                {!editingDreams ? (
                  <p data-testid="pref-value" className="text-sm text-[#111827] bg-[#F9FAFB] rounded-lg p-3 min-h-[80px]">{p.Preferencias || 'Não existem dados'}</p>
                ) : (
                  <textarea data-testid="pref-input" value={dreamsForm.Preferencias} onChange={e => setDreamsForm({ ...dreamsForm, Preferencias: e.target.value })}
                    className="w-full px-3 py-2 text-sm border border-[#E5E7EB] rounded-lg focus:ring-2 focus:ring-[#0F1829] outline-none resize-none" rows={4} placeholder="Preferências..." />
                )}
              </div>
            </div>
          </div>
        </Section>
      )}

      {/* PREFERÊNCIAS E INTERESSES — zona de oportunidades (só visível pela upline) */}
      {isViewingOther && (
        <Section title="Preferências e Interesses (Oportunidades)" testId="profile-preferences">
          <div className="p-5">
            {!prefs || (prefs.total_visitas === 0 && (prefs.compras || []).length === 0) ? (
              <p data-testid="prefs-empty" className="text-sm text-[#6B7280]">
                Ainda sem comportamento registado no site para este cliente. Assim que navegar no site (com rastreio ativo), os interesses aparecem aqui.
              </p>
            ) : (
              <div className="space-y-6">
                {/* KPIs + first-touch */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                  <div className="bg-[#F9FAFB] border border-[#E5E7EB] rounded-lg p-3">
                    <p className="text-[10px] uppercase tracking-[0.15em] text-[#6B7280]">Visitas ao site</p>
                    <p className="text-xl font-bold text-[#111827] font-mono">{prefs.total_visitas || 0}</p>
                  </div>
                  <div className="bg-[#F9FAFB] border border-[#E5E7EB] rounded-lg p-3">
                    <p className="text-[10px] uppercase tracking-[0.15em] text-[#6B7280]">Compras</p>
                    <p className="text-xl font-bold text-[#111827] font-mono">{prefs.num_compras || 0}</p>
                  </div>
                  <div className="bg-[#F9FAFB] border border-[#E5E7EB] rounded-lg p-3">
                    <p className="text-[10px] uppercase tracking-[0.15em] text-[#6B7280]">Total gasto</p>
                    <p className="text-xl font-bold text-[#111827] font-mono">{fmt(prefs.total_gasto)}</p>
                  </div>
                  <div className="bg-[#F9FAFB] border border-[#E5E7EB] rounded-lg p-3">
                    <p className="text-[10px] uppercase tracking-[0.15em] text-[#6B7280] flex items-center gap-1"><Compass size={11} /> Chegou via</p>
                    <p className="text-sm font-semibold text-[#111827] capitalize truncate" title={prefs.first_touch?.origem || '—'}>
                      {prefs.first_touch?.origem || '—'}
                    </p>
                    {prefs.first_touch?.campanha && <p className="text-[10px] text-[#6B7280] truncate">{prefs.first_touch.campanha}</p>}
                  </div>
                </div>

                {/* Temas de interesse */}
                <div>
                  <p className="text-xs uppercase tracking-[0.15em] font-medium text-[#6B7280] mb-2 flex items-center gap-1.5"><Tag size={13} /> Temas de interesse</p>
                  {(prefs.temas || []).length === 0 ? (
                    <p className="text-sm text-[#9CA3AF]">Sem dados.</p>
                  ) : (
                    <div className="flex flex-wrap gap-2" data-testid="prefs-temas">
                      {prefs.temas.map((t, i) => (
                        <span key={`tema-${i}`} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#0F1829]/5 border border-[#0F1829]/10 text-xs text-[#0F1829]">
                          {t.tema}<span className="font-mono font-semibold text-[#6B7280]">{t.visitas}</span>
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                {/* Produtos/cursos de interesse (oportunidade de venda) */}
                <div>
                  <p className="text-xs uppercase tracking-[0.15em] font-medium text-[#6B7280] mb-2 flex items-center gap-1.5"><Storefront size={13} /> Produtos / cursos de interesse</p>
                  {(prefs.produtos_interesse || []).length === 0 ? (
                    <p className="text-sm text-[#9CA3AF]">Ainda não viu páginas de produto específicas.</p>
                  ) : (
                    <div className="border border-[#E5E7EB] rounded-lg overflow-hidden" data-testid="prefs-produtos">
                      <table className="w-full text-sm">
                        <thead><tr className="bg-[#F9FAFB] text-[10px] uppercase tracking-[0.15em] text-[#6B7280]">
                          <th className="text-left px-4 py-2.5 font-medium">Produto / curso</th>
                          <th className="text-right px-3 py-2.5 font-medium w-20">Visitas</th>
                          <th className="text-center px-3 py-2.5 font-medium w-32">Estado</th>
                        </tr></thead>
                        <tbody>
                          {prefs.produtos_interesse.map((pr, i) => (
                            <tr key={`prod-${i}`} className="border-t border-[#F3F4F6] hover:bg-[#F9FAFB]" data-testid={`prefs-produto-${i}`}>
                              <td className="px-4 py-2.5 text-[#111827]">{pr.produto}</td>
                              <td className="px-3 py-2.5 text-right font-mono font-semibold">{pr.visitas}</td>
                              <td className="px-3 py-2.5 text-center">
                                {pr.comprado ? (
                                  <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded bg-[#E7F5EB] text-[#1F7A3B]"><CheckCircle size={11} weight="fill" /> Comprado</span>
                                ) : (
                                  <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded bg-[#FFF4E5] text-[#B45309]"><Target size={11} weight="fill" /> Oportunidade</span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>

                {/* Canais de origem */}
                {(prefs.canais || []).length > 0 && (
                  <div>
                    <p className="text-xs uppercase tracking-[0.15em] font-medium text-[#6B7280] mb-2 flex items-center gap-1.5"><Compass size={13} /> Canais de origem</p>
                    <div className="flex flex-wrap gap-2" data-testid="prefs-canais">
                      {prefs.canais.map((c, i) => (
                        <span key={`canal-${i}`} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#F3F4F6] text-xs text-[#374151] capitalize">
                          {c.origem}<span className="font-mono font-semibold text-[#6B7280]">{c.visitas}</span>
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </Section>
      )}
      <Section title="Subscrições" testId="profile-subscricoes">
        {subscricoes.length > 0 ? (
          <div className="overflow-x-auto">
          <table className="w-full">
            <thead><tr className="border-b border-[#E5E7EB] bg-[#F9FAFB]">
              <th className="text-left text-[10px] uppercase tracking-[0.2em] font-medium text-[#6B7280] px-4 py-2.5">Tipo de Alerta Subscrito</th>
              <th className="text-center text-[10px] uppercase tracking-[0.2em] font-medium text-[#6B7280] px-3 py-2.5">Data Subscrição</th>
            </tr></thead>
            <tbody>{subscricoes.map((s, i) => (
              <tr key={`sub-${s.ID_TipoAlertaSubscrito || i}-${s.DataCriacao || ''}`} data-testid={`subscricao-${i}`} className="border-b border-[#F3F4F6] hover:bg-[#F9FAFB]">
                <td className="px-4 py-2.5 text-sm text-[#111827]">{s.Descricao || '-'}</td>
                <td className="px-3 py-2.5 text-sm text-center text-[#6B7280]">{s.DataCriacao || '-'}</td>
              </tr>
            ))}</tbody>
          </table>
          </div>
        ) : <p className="text-center py-6 text-sm text-[#6B7280]">Sem subscrições</p>}
      </Section>

      {/* 3. VALES */}
      <Section title="Vales" testId="profile-vales">
        {(valesData.Total > 0 || vales.length > 0) && (
          <div className="px-5 py-3 border-b border-[#E5E7EB] flex gap-6">
            <div><span className="text-xs font-medium text-[#6B7280]">Total: </span><span className="text-sm font-mono font-semibold">{fmt(valesData.Total)}</span></div>
            <div><span className="text-xs font-medium text-[#6B7280]">Disponível: </span><span className="text-sm font-mono font-semibold text-[#0F1829]">{fmt(valesData.Disponivel)}</span></div>
            <div><span className="text-xs font-medium text-[#6B7280]">Gasto: </span><span className="text-sm font-mono font-semibold">{fmt(valesData.Gasto)}</span></div>
          </div>
        )}
        {vales.length > 0 ? (
          <div className="overflow-x-auto">
          <table className="w-full">
            <thead><tr className="border-b border-[#E5E7EB] bg-[#F9FAFB]">
              <th className="text-left text-[10px] uppercase tracking-[0.2em] font-medium text-[#6B7280] px-4 py-2.5">Nome Cliente Ativação</th>
              <th className="text-right text-[10px] uppercase tracking-[0.2em] font-medium text-[#6B7280] px-3 py-2.5">Valor</th>
              <th className="text-right text-[10px] uppercase tracking-[0.2em] font-medium text-[#6B7280] px-3 py-2.5">Valor Remanescente</th>
              <th className="text-center text-[10px] uppercase tracking-[0.2em] font-medium text-[#6B7280] px-3 py-2.5">Utilizado</th>
            </tr></thead>
            <tbody>{vales.map((v, i) => (
              <tr key={`vale-${v.ID_Vale || v.Codigo || i}`} data-testid={`vale-${i}`} className="border-b border-[#F3F4F6] hover:bg-[#F9FAFB]">
                <td className="px-4 py-2.5 text-sm text-[#111827]">{v.NomeClienteActivacao || '-'}</td>
                <td className="px-3 py-2.5 text-sm font-mono text-right">{fmt(v.Valor)}</td>
                <td className="px-3 py-2.5 text-sm font-mono text-right">{fmt(v.ValorRemanescente)}</td>
                <td className="px-3 py-2.5 text-sm text-center">{v.Utilizado}</td>
              </tr>
            ))}</tbody>
          </table>
          </div>
        ) : <p className="text-center py-6 text-sm text-[#6B7280]">Sem vales disponíveis</p>}
      </Section>

      {/* 5. MEDIA CENTER */}
      <Section title="MediaCenter" testId="profile-mediacenter">
        {mediacenter.length > 0 ? (
          <div className="overflow-x-auto">
          <table className="w-full">
            <thead><tr className="border-b border-[#E5E7EB] bg-[#F9FAFB]">
              <th className="text-left text-[10px] uppercase tracking-[0.2em] font-medium text-[#6B7280] px-4 py-2.5">Descrição</th>
              <th className="text-center text-[10px] uppercase tracking-[0.2em] font-medium text-[#6B7280] px-3 py-2.5">Nr. Visualizações</th>
              <th className="text-center text-[10px] uppercase tracking-[0.2em] font-medium text-[#6B7280] px-3 py-2.5">Data Disponibilização</th>
              <th className="text-center text-[10px] uppercase tracking-[0.2em] font-medium text-[#6B7280] px-3 py-2.5">Data Última Visualização</th>
            </tr></thead>
            <tbody>{mediacenter.map((m, i) => (
              <tr key={`mc-${m.ID_ConteudoMediaCenter || i}-${m.Descricao || ''}`} data-testid={`media-${i}`} className="border-b border-[#F3F4F6] hover:bg-[#F9FAFB]">
                <td className="px-4 py-2.5 text-sm text-[#111827]">{m.Descricao || '-'}</td>
                <td className="px-3 py-2.5 text-sm text-center">{m.NrVizualizacao || '-'}</td>
                <td className="px-3 py-2.5 text-sm text-center text-[#6B7280]">{m.DataDisponibilizacao || '-'}</td>
                <td className="px-3 py-2.5 text-sm text-center text-[#6B7280]">{m.DataUltimaVizualizacao || '-'}</td>
              </tr>
            ))}</tbody>
          </table>
          </div>
        ) : <p className="text-center py-6 text-sm text-[#6B7280]">Sem mediacenter para apresentar</p>}
      </Section>
    </div>
  );
}
